import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Loader2, TrendingUp, DollarSign, Users, User } from 'lucide-react';
import YearSelector from './YearSelector';
import ExportButton from './ExportButton';
import { useYear } from '../contexts/YearContext';
import { useAuth } from '../contexts/AuthContext';

function BusinessOverview() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const { selectedYear } = useYear();
  const { user } = useAuth();
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1); // 1-12
  const [teamMonthFilter, setTeamMonthFilter] = useState(new Date().getMonth() + 1); // For team view month filter
  const [teamLeaderFilter, setTeamLeaderFilter] = useState('all'); // 'all' or specific leader ID
  const [teamLeaders, setTeamLeaders] = useState([]); // List of team leaders for dropdown
  const [weeklyBreakdown, setWeeklyBreakdown] = useState(null);
  const [loadingWeekly, setLoadingWeekly] = useState(false);
  const [viewMode, setViewMode] = useState('individual'); // 'individual' or 'team'

  // Check if user is a leader (case-insensitive check)
  const isLeader = ['satellite', 'fsl', 'fta'].includes(user?.role?.toLowerCase());
  
  // Check if user can filter by team (Satellite and FSL only, not FTA)
  const canFilterByTeam = ['satellite', 'fsl'].includes(user?.role);

  // Load team leaders for dropdown
  const loadTeamLeaders = useCallback(async () => {
    if (!canFilterByTeam || viewMode !== 'team') return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/team/leaders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTeamLeaders(response.data);
    } catch (error) {
      console.error('Failed to load team leaders:', error);
      setTeamLeaders([]);
    }
  }, [canFilterByTeam, viewMode]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      let url = `${API}/reports/business-overview?year=${selectedYear}`;
      if (viewMode === 'team' && isLeader) {
        // For team view, include the month filter and optional team leader filter
        url = `${API}/reports/team-business-overview?year=${selectedYear}`;
        // Only add month parameter if not YTD (0 means YTD)
        if (teamMonthFilter > 0) {
          url += `&month=${teamMonthFilter}`;
        }
        if (teamLeaderFilter !== 'all') {
          url += `&team_leader_id=${teamLeaderFilter}`;
        }
      }
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(response.data);
    } catch (error) {
      console.error('Failed to load business overview data:', error);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [selectedYear, viewMode, isLeader, teamMonthFilter, teamLeaderFilter]);

  const loadWeeklyBreakdown = useCallback(async () => {
    try {
      setLoadingWeekly(true);
      const token = localStorage.getItem('token');
      
      let url = `${API}/reports/weekly-expense-breakdown?year=${selectedYear}&month=${selectedMonth}`;
      if (viewMode === 'team' && isLeader) {
        url = `${API}/reports/team-weekly-expense-breakdown?year=${selectedYear}&month=${selectedMonth}`;
      }
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWeeklyBreakdown(response.data);
    } catch (error) {
      console.error('Failed to load weekly expense breakdown:', error);
      setWeeklyBreakdown(null);
    } finally {
      setLoadingWeekly(false);
    }
  }, [selectedYear, selectedMonth, viewMode, isLeader]);

  useEffect(() => {
    if (selectedYear) {
      loadData();
    }
  }, [selectedYear, viewMode, teamMonthFilter, teamLeaderFilter, loadData]);

  useEffect(() => {
    if (selectedYear && selectedMonth) {
      loadWeeklyBreakdown();
    }
  }, [selectedYear, selectedMonth, viewMode, loadWeeklyBreakdown]);

  // Load team leaders when switching to team view
  useEffect(() => {
    if (viewMode === 'team' && canFilterByTeam) {
      loadTeamLeaders();
    }
  }, [viewMode, canFilterByTeam, loadTeamLeaders]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const getTotalProfit = () => {
    if (!data || data.length === 0) return 0;
    return data.reduce((sum, month) => sum + month.net_profit, 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  // Calculate totals for table (keep separate)
  const totalAdvanceOnly = data.reduce((sum, month) => sum + month.advance, 0);
  const totalOverrides = data.reduce((sum, month) => sum + (month.overrides || 0), 0);
  // Total Advance for summary card = Advance + Overrides combined
  const totalAdvance = totalAdvanceOnly + totalOverrides;
  const totalMonthly = data.reduce((sum, month) => sum + month.monthly, 0);
  const totalGross = data.reduce((sum, month) => sum + month.gross, 0);
  const totalExpenses = data.reduce((sum, month) => sum + month.expenses, 0);
  const totalNetProfit = data.reduce((sum, month) => sum + month.net_profit, 0);

  return (
    <div className="space-y-6" data-testid="business-overview">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 md:p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-2 flex items-center space-x-2">
              <TrendingUp className="w-6 h-6 md:w-8 md:h-8" />
              <span>
                {viewMode === 'team' && isLeader ? 'Team Business Overview' : 'Business Overview'}
              </span>
            </h1>
            <p className="text-sm md:text-base text-gray-600 dark:text-gray-400">
              {viewMode === 'team' && isLeader 
                ? `Team business performance for ${selectedYear}` 
                : `Monthly business performance for ${selectedYear}`}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
            {/* Team Filter for Satellite and FSL */}
            {viewMode === 'team' && canFilterByTeam && (
              <div className="w-full sm:w-56">
                <div className="bg-blue-100 dark:bg-blue-900/30 px-3 py-1 rounded-t-lg mb-1">
                  <label className="text-sm md:text-base font-extrabold text-blue-900 dark:text-blue-300">FILTER BY TEAM</label>
                </div>
                <select
                  value={teamLeaderFilter}
                  onChange={(e) => setTeamLeaderFilter(e.target.value)}
                  className="w-full px-3 py-3 md:py-2 border-2 border-blue-300 dark:border-blue-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-medium text-base"
                >
                  <option value="all">All Teams</option>
                  {teamLeaders.map((leader) => (
                    <option key={leader.id} value={leader.id}>
                      {leader.team_name || leader.name} ({leader.role.toUpperCase()})
                    </option>
                  ))}
                </select>
              </div>
            )}
            
            {/* Month Selector for Team View */}
            {viewMode === 'team' && isLeader && (
              <div className="w-full sm:w-48">
                <div className="bg-green-100 dark:bg-green-900/30 px-3 py-1 rounded-t-lg mb-1">
                  <label className="text-sm md:text-base font-extrabold text-green-900 dark:text-green-300">SELECT PERIOD</label>
                </div>
                <select
                  value={teamMonthFilter}
                  onChange={(e) => setTeamMonthFilter(parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-3 md:py-2 border-2 border-green-300 dark:border-green-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-medium text-base"
                >
                  <option value={0}>YTD (Year to Date)</option>
                  <option value={1}>January</option>
                  <option value={2}>February</option>
                  <option value={3}>March</option>
                  <option value={4}>April</option>
                  <option value={5}>May</option>
                  <option value={6}>June</option>
                  <option value={7}>July</option>
                  <option value={8}>August</option>
                  <option value={9}>September</option>
                  <option value={10}>October</option>
                  <option value={11}>November</option>
                  <option value={12}>December</option>
                </select>
              </div>
            )}
            
            {/* View Mode Toggle - Only show for leaders */}
            {isLeader && (
              <div className="flex items-center space-x-2 w-full sm:w-auto">
                <User className="w-4 h-4 text-gray-600 dark:text-gray-300 hidden sm:block" />
                <div className="flex bg-gray-100 dark:bg-gray-700 rounded-lg p-1 w-full sm:w-auto">
                  <button
                    onClick={() => setViewMode('individual')}
                    className={`flex-1 sm:flex-none px-4 sm:px-3 py-2 sm:py-1 rounded-md text-sm font-medium transition-colors ${
                      viewMode === 'individual'
                        ? 'bg-white text-blue-600 shadow-sm'
                        : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:text-white'
                    }`}
                  >
                    Individual
                  </button>
                  <button
                    onClick={() => setViewMode('team')}
                    className={`flex-1 sm:flex-none px-4 sm:px-3 py-2 sm:py-1 rounded-md text-sm font-medium transition-colors ${
                      viewMode === 'team'
                        ? 'bg-white text-blue-600 shadow-sm'
                        : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:text-white'
                    }`}
                  >
                    <Users className="w-4 h-4 inline mr-1" />
                    Team
                  </button>
                </div>
              </div>
            )}
            <YearSelector />
            {viewMode === 'individual' && (
              <ExportButton
                endpoint="/exports/business-overview.csv"
                params={{ year: selectedYear }}
                filename={`papertrail-business-overview-${selectedYear}.csv`}
                label="Export CSV"
                testId="export-business-overview-csv-btn"
              />
            )}
          </div>
        </div>
      </div>

      {/* Formula Banner */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 rounded p-4">
        <p className="text-sm font-semibold text-blue-900 dark:text-blue-200 mb-2">💡 How this page is calculated</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm text-gray-800 dark:text-gray-100">
          <div className="p-3 bg-white dark:bg-gray-800 rounded">
            <p className="text-xs uppercase text-gray-500 dark:text-gray-400 mb-1">Gross</p>
            <p className="font-mono text-[13px]"><strong>Advance</strong> + <strong>Monthly</strong>{isLeader ? ' + Overrides' : ''}</p>
            <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">Total business income before any expenses.</p>
          </div>
          <div className="p-3 bg-white dark:bg-gray-800 rounded">
            <p className="text-xs uppercase text-gray-500 dark:text-gray-400 mb-1">Expenses</p>
            <p className="font-mono text-[13px]">Sum of rows tagged to <strong>Business Expense</strong></p>
            <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">Pulled directly from the Expense Tracker.</p>
          </div>
          <div className="p-3 bg-white dark:bg-gray-800 rounded">
            <p className="text-xs uppercase text-gray-500 dark:text-gray-400 mb-1">Net Profit</p>
            <p className="font-mono text-[13px]"><strong>Gross</strong> − <strong>Expenses</strong></p>
            <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">Your business bottom line for the month.</p>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <SummaryCard
          label="Total Advance"
          value={totalAdvance}
          color="blue"
          tooltip={isLeader
            ? 'Your weekly paycheck income + any overrides (full amount, before allocation splits).'
            : 'Your weekly paycheck income (full amount, before allocation splits).'}
        />
        <SummaryCard
          label="Total Monthly"
          value={totalMonthly}
          color="purple"
          tooltip="Total of your Monthly Checks (full amount, before allocation splits)."
        />
        <SummaryCard
          label="Total Gross"
          value={totalGross}
          color="green"
          tooltip={`Advance + Monthly${isLeader ? ' + Overrides' : ''}. Your full business income for the period.`}
        />
        <SummaryCard
          label="Total Expenses"
          value={totalExpenses}
          color="red"
          tooltip="Business expenses only — pulled from the Expense Tracker (Business Expense account)."
        />
        <SummaryCard
          label="Total Net Profit"
          value={totalNetProfit}
          color="teal"
          tooltip="Gross − Expenses. Your business bottom line."
        />
      </div>

      {/* Business Overview Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="business-overview-table">
            <thead className="bg-gray-800 text-white">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold">
                  {viewMode === 'team' && isLeader ? 'Agent Name' : 'Month'}
                </th>
                <th className="px-4 py-3 text-right text-sm font-semibold" title="Your weekly paycheck income (full amount, before allocation splits)">Advance</th>
                {isLeader && (
                  <th className="px-4 py-3 text-right text-sm font-semibold" title="Override commissions from your downline">Override</th>
                )}
                <th className="px-4 py-3 text-right text-sm font-semibold" title="Monthly commission checks / bonuses (full amount)">Monthly</th>
                <th className="px-4 py-3 text-right text-sm font-semibold bg-green-700" title={`Gross = Advance + Monthly${isLeader ? ' + Overrides' : ''}`}>Gross</th>
                <th className="px-4 py-3 text-right text-sm font-semibold" title="Business expenses tagged in the Expense Tracker">Expenses</th>
                <th className="px-4 py-3 text-right text-sm font-semibold bg-blue-700" title="Net Profit = Gross − Expenses">Net Profit</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {data.map((row, index) => (
                <tr 
                  key={row.agent_id || row.agent_name || row.month || `row-${index}`} 
                  className={`hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${index % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-700'}`}
                >
                  <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">
                    {viewMode === 'team' && isLeader ? row.agent_name : row.month}
                  </td>
                  <td className="px-4 py-3 text-right text-blue-700 dark:text-blue-400 font-semibold">
                    {formatCurrency(row.advance)}
                  </td>
                  {isLeader && (
                    <td className="px-4 py-3 text-right text-purple-700 dark:text-purple-400 font-semibold">
                      {formatCurrency(row.overrides || 0)}
                    </td>
                  )}
                  <td className="px-4 py-3 text-right text-purple-700 dark:text-purple-400 font-semibold">
                    {formatCurrency(row.monthly)}
                  </td>
                  <td className="px-4 py-3 text-right text-green-800 dark:text-green-400 font-bold bg-green-50 dark:bg-green-900/20">
                    {formatCurrency(row.gross)}
                  </td>
                  <td className="px-4 py-3 text-right text-red-700 dark:text-red-400 font-semibold">
                    {formatCurrency(row.expenses)}
                  </td>
                  <td className="px-4 py-3 text-right text-blue-800 dark:text-blue-400 font-bold bg-blue-50 dark:bg-blue-900/20">
                    {formatCurrency(row.net_profit)}
                  </td>
                </tr>
              ))}
              
              {/* Total Row */}
              <tr className="bg-gray-900 text-white font-bold">
                <td className="px-4 py-4 text-left">TOTAL</td>
                <td className="px-4 py-4 text-right">{formatCurrency(totalAdvanceOnly)}</td>
                {isLeader && (
                  <td className="px-4 py-4 text-right">{formatCurrency(totalOverrides)}</td>
                )}
                <td className="px-4 py-4 text-right">{formatCurrency(totalMonthly)}</td>
                <td className="px-4 py-4 text-right bg-green-800">{formatCurrency(totalGross)}</td>
                <td className="px-4 py-4 text-right">{formatCurrency(totalExpenses)}</td>
                <td className="px-4 py-4 text-right bg-blue-800">{formatCurrency(totalNetProfit)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Legend */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <h3 className="font-semibold text-blue-900 dark:text-blue-200 mb-2">📊 Overview Explanation</h3>
        <ul className="text-sm text-blue-800 dark:text-blue-200 space-y-1">
          <li><strong>Advance:</strong> Your weekly paycheck income — the full check you received (not an allocation slice). Logged on the Weekly Income page.</li>
          {isLeader && (
            <li><strong>Overrides:</strong> Override commissions earned from your downline; shown as a separate column and also rolled into "Total Advance" above.</li>
          )}
          <li><strong>Monthly:</strong> Monthly commission checks / bonuses — the full amount (not an allocation slice). Logged on the Monthly Checks page.</li>
          <li><strong>Gross:</strong> Total business income for the period. <span className="font-mono">Gross = Advance + Monthly{isLeader ? ' + Overrides' : ''}</span>.</li>
          <li><strong>Expenses:</strong> Business expenses tagged to the <em>Business Expense</em> account in the Expense Tracker. Personal / tax / savings expenses are intentionally excluded from this page.</li>
          <li><strong>Net Profit:</strong> Your business bottom line. <span className="font-mono">Net Profit = Gross − Expenses</span>.</li>
        </ul>
        <p className="text-xs text-blue-700 dark:text-blue-300 mt-3">
          <strong>Note on scope:</strong> this page is <em>business-only</em>. Reports → Net Profit subtracts both Business <em>and</em> Personal expenses from Gross; this page subtracts only Business Expenses. Both pages pull from the same Expense Tracker — the difference is purely what gets included in "Expenses".
        </p>
      </div>

      {/* Weekly Expense Breakdown by Category - Only show in Individual view */}
      {viewMode === 'individual' && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 md:p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0 mb-4">
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white">Weekly Expense Breakdown by Category</h2>
            <div className="w-full md:w-64">
              <div className="bg-purple-100 dark:bg-purple-900/30 px-3 py-1 rounded-t-lg mb-1">
                <label className="text-sm md:text-base font-extrabold text-purple-900 dark:text-purple-300">SELECT MONTH</label>
              </div>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                className="w-full px-3 py-3 md:py-2 border-2 border-purple-300 dark:border-purple-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-medium text-base"
              >
                <option value={1}>January</option>
                <option value={2}>February</option>
                <option value={3}>March</option>
                <option value={4}>April</option>
                <option value={5}>May</option>
                <option value={6}>June</option>
                <option value={7}>July</option>
                <option value={8}>August</option>
                <option value={9}>September</option>
                <option value={10}>October</option>
                <option value={11}>November</option>
                <option value={12}>December</option>
              </select>
            </div>
          </div>

          {loadingWeekly ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          ) : weeklyBreakdown ? (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-800 text-white">
                    <th className="border border-gray-600 px-3 py-2 text-left text-sm font-semibold">Category</th>
                    {weeklyBreakdown.weeks.map((week, idx) => (
                      <th key={`week-header-${week}-${idx}`} className="border border-gray-600 px-3 py-2 text-center text-sm font-semibold whitespace-nowrap">
                        {week}
                      </th>
                    ))}
                    <th className="border border-gray-600 px-3 py-2 text-center text-sm font-semibold bg-blue-700">
                      Monthly Total
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {/* Category Rows */}
                  {weeklyBreakdown.categories.map((category, catIdx) => (
                    <tr key={category.category || `cat-${catIdx}`} className={catIdx % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-700'}>
                      <td className="border border-gray-300 dark:border-gray-600 px-3 py-2 font-medium text-gray-900 dark:text-white">
                        {category.category}
                      </td>
                      {category.weeks.map((amount, weekIdx) => (
                        <td key={`${category.category}-${weeklyBreakdown.weeks[weekIdx] || weekIdx}`} className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-center text-gray-700 dark:text-gray-300">
                          {amount > 0 ? formatCurrency(amount) : '-'}
                        </td>
                      ))}
                      <td className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-center font-semibold text-blue-700 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20">
                        {category.monthly_total > 0 ? formatCurrency(category.monthly_total) : '-'}
                      </td>
                    </tr>
                  ))}

                  {/* Grand Totals Row */}
                  <tr className="bg-yellow-100 dark:bg-yellow-900/30 font-bold">
                    <td className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-gray-900 dark:text-white">Grand Totals</td>
                    {weeklyBreakdown.week_totals.map((total, idx) => (
                      <td key={`week-total-${weeklyBreakdown.weeks[idx] || idx}`} className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-center text-gray-900 dark:text-white">
                        {formatCurrency(total)}
                      </td>
                    ))}
                    <td className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-center text-blue-900 bg-blue-100 font-bold">
                      {formatCurrency(weeklyBreakdown.grand_total)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center text-gray-500 dark:text-gray-400 py-8">
              Select a month to view weekly expense breakdown
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SummaryCard({ label, value, color, tooltip }) {
  const colorClasses = {
    blue: 'bg-blue-50 dark:bg-blue-900/20 text-blue-900 dark:text-blue-300 border-blue-200 dark:border-blue-800',
    purple: 'bg-purple-50 dark:bg-purple-900/20 text-purple-900 dark:text-purple-300 border-purple-200 dark:border-purple-800',
    green: 'bg-green-50 dark:bg-green-900/20 text-green-900 dark:text-green-300 border-green-200 dark:border-green-800',
    red: 'bg-red-50 dark:bg-red-900/20 text-red-900 dark:text-red-300 border-red-200 dark:border-red-800',
    teal: 'bg-teal-50 dark:bg-teal-900/20 text-teal-900 dark:text-teal-300 border-teal-200 dark:border-teal-800',
  };

  return (
    <div
      className={`${colorClasses[color]} border rounded-lg p-4 shadow-sm transition-colors`}
      title={tooltip}
    >
      <p className="text-sm font-medium mb-1">{label}</p>
      <p className="text-2xl font-bold">
        {new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 2
        }).format(value)}
      </p>
      {tooltip && (
        <p className="text-[11px] opacity-75 mt-1 leading-snug">{tooltip}</p>
      )}
    </div>
  );
}

export default BusinessOverview;
